import axios from "axios";
import Vue from "vue";
import {
  DEFAULT_BOOTSTRAP_RETRY_CONFIG,
  getRetryDelayMs,
  shouldRetryBootstrapError,
  sleep,
} from "./retryPolicy";
import {
  activateMaintenanceMode,
  clearMaintenanceMode,
  resetAccessGate,
} from "../services/accessGate";

class CassClient {
  // TODO(owner-ref-migration): Replace this handwritten client with generated OpenAPI client after migration stabilization.
  instance = axios.create({
    baseURL: process.env.VUE_APP_CASS_URL,
    timeout: 10000,
  });

  constructor() {
    console.log("CassClient constructor");

    this.instance.interceptors.request.use(
      async (config) => {
        // Do something before request is sent
        let token = await Vue.prototype.$authService.getAccessToken({
          scopes: [process.env.VUE_APP_CASS_SCOPE],
        });
        config.headers.Authorization = `Bearer ${token.accessToken}`;
        return config;
      },
      function (error) {
        // Do something with request error
        return Promise.reject(error);
      }
    );

    this.instance.interceptors.response.use(
      function (response) {
        if (response?.config?.maintenanceBootstrapCall === true) {
          clearMaintenanceMode();
        }
        return response;
      },
      async (error) => {
        const errorCode = error?.response?.data?.errorCode || error?.errorCode;
        if (errorCode === "MAINTENANCE_MODE_ACTIVE") {
          activateMaintenanceMode({
            source: "cass",
            path: error?.config?.url,
            status: error?.response?.status,
          });
        }

        const requestConfig = error?.config;
        const retryOptions = requestConfig?.bootstrapRetry;
        if (!retryOptions || !shouldRetryBootstrapError(error)) {
          return Promise.reject(error);
        }

        const maxRetries =
          retryOptions.maxRetries ?? DEFAULT_BOOTSTRAP_RETRY_CONFIG.maxRetries;
        const baseDelayMs =
          retryOptions.baseDelayMs ?? DEFAULT_BOOTSTRAP_RETRY_CONFIG.baseDelayMs;
        const currentAttempt = requestConfig.__bootstrapRetryCount || 0;
        if (currentAttempt >= maxRetries) {
          return Promise.reject(error);
        }

        requestConfig.__bootstrapRetryCount = currentAttempt + 1;
        const delayMs = getRetryDelayMs(baseDelayMs, currentAttempt);
        console.warn(
          "Retrying bootstrap request",
          JSON.stringify({
            attempt: currentAttempt + 1,
            maxRetries,
            status: error?.response?.status,
            errorCode: error?.response?.data?.errorCode,
            delayMs,
          })
        );
        await sleep(delayMs);
        return this.instance(requestConfig);
      }
    );
  }
  resetSessionState() {
    resetAccessGate();
  }
  handleError(error) {
    let err;
    console.log(error);
    if (error.response && error.response.data) {
      err = error.response.data;
      const statusCode = error.response.status;
      const errorCode = err.errorCode;
      err.status = statusCode;
      if (
        statusCode === 403 &&
        errorCode === "AUTH_USER_NOT_IN_REQUIRED_GROUP"
      ) {
        err.message =
          "You do not have all needed permissions to use the Consumer Application Self Service. Please contact api-mgmt-support@schaeffler.com to get needed group membership.";
      }
    } else {
      err = {
        message: "network error occurred. please retry.",
        requestId: "not available",
      };
    }
    // This feature enables slot buttons to do something which needs more time
    err.loading = false;
    return err;
  }
  async getApplications() {
    console.log("getApplications");
    return this.instance.get("applications", { timeout: 35000 });
  }
  getDeletedAppliations() {
    console.log("getDeletedAppliations");
    return this.instance.get("deletedapplications", { timeout: 35000 });
  }
  realDeleteDeletedAppliations(id) {
    console.log("realDeleteDeletedAppliations");
    return this.instance.delete("deletedapplications/" + id, {
      timeout: 35000,
    });
  }
  getApplication(id) {
    console.log("getApplications");
    return this.instance.get("applications/" + id, { timeout: 35000 });
  }
  getEntraOwnership(id) {
    return this.instance.get("applications/" + id + "/entra-owners/me", { timeout: 50000 });
  }
  addEntraOwner(id) {
    return this.instance.post("applications/" + id + "/entra-owners/me", {}, { timeout: 50000 });
  }
  removeEntraOwner(id) {
    return this.instance.delete("applications/" + id + "/entra-owners/me", { timeout: 50000 });
  }
  patchApplication(id, body) {
    console.log("patchApplication");
    return this.instance.patch("applications/" + id, body, { timeout: 35000 });
  }
  getProducts(env) {
    console.log("getProducts");
    return this.instance.get("sapproducts", {
      params: { environment: env },
    });
  }
  getProduct(env, productid) {
    console.log("getProduct " + productid);
    return this.instance.get("sapproducts/" + productid, {
      params: { environment: env },
    });
  }
  sendData(data) {
    console.debug("sendData");
    return this.instance.post("data", data, {
      headers: {
        "Content-Type": "text/plain",
      },
    });
  }
  getSAPStatus() {
    console.debug("getSAPStatus");
    return this.instance.get("status", {});
  }
  getAdminSAPAPIStatus() {
    return this.instance.get("admin/sapapi/status", { timeout: 10000 });
  }
  getAdminSAPAPIStatusHistory() {
    return this.instance.get("admin/sapapi/status/history", { timeout: 10000 });
  }
  patchAdminSAPAPIStatusOverride(status, reason) {
    const payload = { reason: (reason || "").trim() };
    if (status) {
      payload.status = status;
    }
    return this.instance.patch("admin/sapapi/status/override", payload, {
      timeout: 10000,
    });
  }
  triggerIdentityResolverRefresh() {
    return this.instance.post("admin/identity-resolver/refresh", {}, {
      timeout: 10000,
    });
  }
  getIdentityResolverRefreshState() {
    return this.instance.get("admin/identity-resolver/refresh", {
      timeout: 10000,
    });
  }
  getIdentityResolverCacheState() {
    return this.instance.get("admin/identity-resolver/cache", {
      timeout: 10000,
    });
  }
  searchIdentityResolverCache(
    query,
    page = 1,
    pageSize = 20,
    unresolvedOnly = false,
    ambiguousOnly = false
  ) {
    const params = {
      page,
      pageSize,
      unresolvedOnly,
      ambiguousOnly,
    };
    if (query && query.trim() !== "") {
      params.q = query.trim();
    }
    return this.instance.get("admin/identity-resolver/cache/search", {
      params,
      timeout: 10000,
    });
  }
  searchIdentityResolverAppOwners(
    query,
    page = 1,
    pageSize = 20,
    unresolvedOnly = false,
    ambiguousOnly = false
  ) {
    const params = {
      page,
      pageSize,
      unresolvedOnly,
      ambiguousOnly,
    };
    if (query && query.trim() !== "") {
      params.q = query.trim();
    }
    return this.instance.get("admin/identity-resolver/app-owners/search", {
      params,
      timeout: 10000,
    });
  }
  createApplication(obj) {
    console.log("createApplication");
    return this.instance.post("applications", obj, { timeout: 35000 });
  }
  getApplicationCreationOperation(operationId) {
    return this.instance.get(
      "application-creation-operations/" + encodeURIComponent(operationId),
      { timeout: 35000 }
    );
  }
  getAdminApplicationCreationOperations({ search = "", failedOnly = false, cursor = "", limit = 25 } = {}) {
    const params = { search: (search || "").trim(), failedOnly, cursor, limit };
    return this.instance.get("admin/application-creation-operations", { params, timeout: 10000 });
  }
  getAdminApplicationCreationOperation(operationId) {
    return this.instance.get(
      `admin/application-creation-operations/${encodeURIComponent(operationId)}`,
      { timeout: 10000 }
    );
  }
  assessAdminApplicationCreationOperation(operationId) {
    return this.instance.post(
      `admin/application-creation-operations/${encodeURIComponent(operationId)}/assessment`,
      {},
      { timeout: 35000 }
    );
  }
  releaseAdminApplicationCreationOperation(operationId, reason) {
    const trimmedReason = (reason || "").trim();
    const payload = trimmedReason ? { reason: trimmedReason } : {};
    return this.instance.post(
      `admin/application-creation-operations/${encodeURIComponent(operationId)}/release`,
      payload,
      { timeout: 10000 }
    );
  }
  deleteAdminApplicationCreationOperation(operationId) {
    return this.instance.delete(
      `admin/application-creation-operations/${encodeURIComponent(operationId)}`,
      { timeout: 10000 }
    );
  }
  getPermissions(id) {
    console.log("getPermissions");
    return this.instance.get("applications/" + id + "/permissions", {
      timeout: 35000,
    });
  }
  getPermission(appid, permid) {
    console.log("getPermissions");
    return this.instance.get(
      "applications/" + appid + "/permissions/" + permid,
      { timeout: 35000 }
    );
  }
  grantPermission(appid, permid, grant) {
    console.log("grantPermission");
    return this.instance.patch(
      "applications/" + appid + "/permissions/" + permid,
      grant,
      {}
    );
  }
  denyPermission(appid, permid, deny) {
    console.log("grantPermission");
    return this.instance.patch(
      "applications/" + appid + "/permissions/" + permid,
      deny,
      {}
    );
  }
  getITDemands() {
    console.log("getITDemands");
    return this.instance.get("itsecs", { timeout: 35000 });
  }
  addPermission(appid, perm, reason) {
    console.log("addPermission");
    let permission = { name: perm, reason: reason };
    return this.instance.post(
      "applications/" + appid + "/permissions",
      permission,
      {}
    );
  }
  deletePermission(appid, permid) {
    console.log("deletePermission");
    return this.instance.delete(
      "applications/" + appid + "/permissions/" + permid,
      {}
    );
  }
  getAppMetrics(applicationid) {
    console.log("getAppMetrics: ", applicationid);
    //applicationid = "df31849d-8aa3-432d-acac-d4fae60b90b6";
    return this.instance.get("applications/" + applicationid + "/metrics", {});
  }
  getUser() {
    console.log("getUser");
    return this.instance.get("users/me", {
      maintenanceBootstrapCall: true,
      bootstrapRetry: {
        maxRetries: DEFAULT_BOOTSTRAP_RETRY_CONFIG.maxRetries,
        baseDelayMs: DEFAULT_BOOTSTRAP_RETRY_CONFIG.baseDelayMs,
      },
    });
  }
  getMaintenanceMode() {
    return this.instance.get("admin/maintenance", { timeout: 10000 });
  }
  patchMaintenanceMode(enabled) {
    return this.instance.patch(
      "admin/maintenance",
      { enabled },
      { timeout: 10000 }
    );
  }
  getBackgroundJobs() {
    return this.instance.get("admin/background-jobs", { timeout: 10000 });
  }
  getBackgroundJob(jobId) {
    return this.instance.get(`admin/background-jobs/${jobId}`, { timeout: 10000 });
  }
  patchBackgroundJob(jobId, payload) {
    return this.instance.patch(`admin/background-jobs/${jobId}`, payload, {
      timeout: 10000,
    });
  }
  runBackgroundJob(jobId, reason) {
    return this.instance.post(
      `admin/background-jobs/${jobId}/run`,
      { reason: (reason || "").trim() },
      { timeout: 10000 }
    );
  }
  getBackgroundJobAudit(limit = 50) {
    return this.instance.get("admin/background-jobs/audit", {
      params: { limit },
      timeout: 10000,
    });
  }
  searchAuthFailures(payload) {
    return this.instance.post("admin/auth-failures/search", payload, {
      timeout: 10000,
    });
  }
  getAuthFailure(id) {
    return this.instance.get(`admin/auth-failures/${encodeURIComponent(id)}`, {
      timeout: 10000,
    });
  }
  deleteAuthFailure(id) {
    return this.instance.delete(`admin/auth-failures/${encodeURIComponent(id)}`, {
      timeout: 10000,
    });
  }
  bulkDeleteAuthFailures(filter) {
    return this.instance.post(
      "admin/auth-failures/delete",
      { filter },
      { timeout: 10000 }
    );
  }
  patchAuthFailureRetention(id, keepUntil) {
    return this.instance.patch(
      `admin/auth-failures/${encodeURIComponent(id)}/retention`,
      { keepUntil },
      { timeout: 10000 }
    );
  }
  getAuthFailureSignals() {
    return this.instance.get("admin/auth-failures/signals", {
      timeout: 10000,
    });
  }
  patchUser(user) {
    console.log("PatchUser");
    return this.instance.patch("users", user, {});
  }
  getAllUsers() {
    console.log("getAllUsers");
    return this.instance.get("users", { timeout: 55000 });
  }
  getPermissionEvents(state) {
    console.log("getPermissionEvents");
    return this.instance.get("/permissionevent/" + state, { timeout: 55000 });
  }
  getVersion() {
    console.log("getVersion");
    return this.instance.get("version", {});
  }
  getSAPAPIVersion() {
    console.log("getSAPAPIVersion");
    return this.instance.get("sapapi/version", {});
  }
  getCSR(appid, certid) {
    console.log("getCSR");
    return this.instance.get(
      "applications/" + appid + "/certificates/" + certid + "/csr",
      { responseType: "blob" }
    );
  }
  uploadCSR(appid, certid, csrData) {
    console.log("uploadCSR");
    return this.instance.post(
      "applications/" + appid + "/certificates/" + certid + "/csr",
      csrData,
      {
        headers: {
          "Content-Type": "text/plain",
        },
      }
    );
  }
  getSignedCert(appid, certid) {
    console.log("getSignedCert");
    return this.instance.get(
      "applications/" + appid + "/certificates/" + certid + "/signedCert",
      { responseType: "blob" }
    );
  }
  uploadSignedCert(appid, certid, certdata) {
    console.log("uploadSignedCert");
    return this.instance.post(
      "applications/" + appid + "/certificates/" + certid + "/signedCert",
      certdata,
      {
        headers: {
          "Content-Type": "text/plain",
        },
      }
    );
  }
  addOwner(appid, owner) {
    console.log("addOwner");
    let ownerRequest = { ownerRef: owner };
    return this.instance.post(
      "applications/" + appid + "/owners",
      ownerRequest,
      { timeout: 30000 }
    );
  }
  searchOwners(appid, query, page = 1, pageSize = 20) {
    return this.instance.get("applications/" + appid + "/owners/search", {
      params: {
        q: query,
        page: page,
        pageSize: pageSize,
      },
      timeout: 30000,
    });
  }
  deleteOwner(appid, owner) {
    console.log(owner);
    const encodedOwnerRef = encodeURIComponent(owner);
    return this.instance.delete(
      "applications/" + appid + "/owners/" + encodedOwnerRef,
      { timeout: 30000 }
    );
  }
  getSecrets(appid) {
    console.log("getSecrets");
    return this.instance.get("applications/" + appid + "/secrets", {});
  }
  addSecret(appid) {
    console.log("addSecret");
    return this.instance.post("applications/" + appid + "/secrets", {});
  }
  deleteSecret(appid, secid) {
    console.log("deleteSecret");
    return this.instance.delete(
      "applications/" + appid + "/secrets/" + secid,
      {}
    );
  }
  patchSecret(appid, secid, body) {
    console.log("patchSecret");
    return this.instance.patch(
      "applications/" + appid + "/secrets/" + secid,
      body,
      {}
    );
  }
  addCertificate(appid) {
    console.log("addCertificate");
    return this.instance.post("applications/" + appid + "/certificates", {});
  }
  patchCertificate(appid, certid, body) {
    console.log("patchCertificate");
    return this.instance.patch(
      "applications/" + appid + "/certificates/" + certid,
      body,
      {}
    );
  }
  addCSR(appid, certid, csr) {
    console.log("addCSR");
    return this.instance.post(
      "applications/" + appid + "/certificates/" + certid + "/csr",
      csr,
      { timeout: 55000 }
    );
  }
  deleteCertificate(appid, certid) {
    console.log("deleteCertificate");
    return this.instance.delete(
      "applications/" + appid + "/certificates/" + certid,
      {}
    );
  }
  addRedirectURI(appid, redirect) {
    console.log("addRedirectURI");
    return this.instance.post(
      "applications/" + appid + "/redirectURIs",
      redirect,
      {}
    );
  }
  deleteRedirectURI(appid, uriid) {
    console.log("deleteRedirectURI");
    return this.instance.delete(
      "applications/" + appid + "/redirectURIs/" + uriid,
      {}
    );
  }
  deleteApplication(appid, safeDelete) {
    console.log("deleteApplication");
    return this.instance.delete("applications/" + appid, {
      timeout: 35000,
      params: { safeDelete: safeDelete },
    });
  }
  getSubscriptions(appid) {
    console.log("getSubscriptions");
    return this.instance.get("applications/" + appid + "/subscriptions", {
      timeout: 35000,
    });
  }
  addSubscription(appid, productname) {
    console.log("addSubscription");
    return this.instance.post(
      "applications/" + appid + "/subscriptions/" + productname,
      null,
      {
        timeout: 35000,
      }
    );
  }
  subscriptionAssistance(appid, body) {
    console.log("subscriptionAssistance");
    return this.instance.post(
      "applications/" + appid + "/subscriptions/assistance",
      body,
      {
        timeout: 35000,
      }
    );
  }
  deleteSubscription(appid, productname) {
    console.log("deleteSubscription");
    return this.instance.delete(
      "applications/" + appid + "/subscriptions/" + productname,
      null,
      {
        timeout: 35000,
      }
    );
  }
  downloadSwagger(proxyname, environment) {
    const params = new URLSearchParams([["environment", environment]]);
    console.log("downloadSwagger");
    return this.instance.get("api/" + proxyname + "/documentation", {
      params,
      responseType: "arraybuffer",
    });
  }
  getSAPApplications() {
    console.log("getSAPApplications");
    return this.instance.get("sapapplications", { timeout: 35000 });
  }
  getSAPApplication(id, environment) {
    console.log("getSAPApplication for id: " + id + " and env: " + environment);
    const params = new URLSearchParams([["environment", environment]]);
    return this.instance.get("sapapplications/" + id, {
      params,
      timeout: 35000,
    });
  }
  deleteSAPApplication(id, environment) {
    console.log(
      "deleteSAPApplication with id: " + id + " and env: " + environment
    );
    const params = new URLSearchParams([["environment", environment]]);
    return this.instance.delete("sapapplications/" + id, {
      params,
      timeout: 35000,
    });
  }
}

const cass = new CassClient();

export default cass;
